/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  EuiButtonEmpty,
  EuiCallOut,
  EuiHealth,
  EuiInMemoryTable,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  useDeleteRoleMappingsIndividually,
  useOrganizationIdp,
  useTestRoleMappings,
} from '@modules/security-idp-lib/hooks'
import type { RoleAssignments, RoleMapping } from '@modules/cloud-api/v1/types'
import { addToast } from '@modules/cui/Toasts'
import { isOrgOwnerRoleAssignments } from '@modules/role-assignments-lib'

import RoleBadges from '@/components/Organization/RoleBadges'
import { organizationRoleMappingUrl } from '@/apps/userconsole/urls'
import { initiateOrganizationSsoRedirect } from '@/actions/auth/auth'
import SsoError from '@/components/Sso/SsoError'

import { useIdTokenAndStateFromHash } from '../hooks'
import VerificationResult from '../Components/VerificationResult'

import { errorMessages, messages } from './messages'
import ConfirmRemovingRoleMappingModal from './ConfirmRemovingRoleMappingModal'
import { checkHasToRunValidation, sorting } from './lib'

import type { ReactNode } from 'react'
import type { Props, SsoErrorState } from './types'

const afterRemovingRoleMapping = ({
  removedMappingName,
  onSettled,
  setError,
}: {
  removedMappingName: string
  onSettled: () => void
  setError: (error: ReactNode) => void
}) => ({
  onSuccess: () => {
    addToast({
      family: 'organization.security.role-mappings-table.remove',
      color: 'success',
      iconType: 'check',
      title: (
        <FormattedMessage
          {...messages.toastRemoveRoleMappingSuccess}
          values={{
            roleMappingName: removedMappingName,
          }}
        />
      ),
    })
  },
  onError: (error) => {
    if (error.errors?.[0]?.code === 'org.role_mapping_rule.missing_owner_role') {
      setError(<FormattedMessage {...errorMessages.missingOrgOwnerRole} />)
      return
    }

    addToast({
      family: 'organization.security.role-mappings-table.remove',
      color: 'danger',
      iconType: 'alert',
      title: (
        <FormattedMessage
          {...messages.toastRemoveRoleMappingError}
          values={{ roleMappingName: removedMappingName }}
        />
      ),
    })
  },
  onSettled,
})

const RoleMappingTable = ({
  organizationId,
  isLoading,
  roleMappings = [],
  hasDeleteRoleMappingPermission,
  hasUpdateRoleMappingPermission,
  readonly,
}: Props) => {
  const { formatMessage } = useIntl()
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false)
  const [error, setError] = React.useState<ReactNode | null>(null)
  const [selectedRoleMapping, setSelectedRoleMapping] = React.useState<RoleMapping | undefined>(
    undefined,
  )
  const [ssoError, setSsoError] = React.useState<SsoErrorState | null>(null)
  const navigate = useNavigate()
  const { isLoading: isLoadingIdp, data: idpInfo } = useOrganizationIdp(organizationId)
  const { mutate: deleteRoleMappingMutate, isLoading: isDeletingRoleMapping } =
    useDeleteRoleMappingsIndividually()
  const {
    mutate: testRoleMappings,
    data: testResult,
    reset: resetTestResult,
  } = useTestRoleMappings()
  const {
    idToken,
    state: hashState,
    error: errorFromHash,
    errorDescription: errorDescriptionFromHash,
  } = useIdTokenAndStateFromHash()
  const location = useLocation()

  // Handle successful SSO flow with role mapping removal
  useEffect(() => {
    const { roleMappingNameToRemove } = hashState || {}

    if (idToken && roleMappingNameToRemove && roleMappings.length > 0) {
      setSelectedRoleMapping(roleMappings.find((rm) => rm.name === roleMappingNameToRemove))
      initiateTestRoleMappings({ roleMappingNameToRemove })
      // Clear the hash state after processing
      navigate(location.pathname + location.search, { replace: true })
    }
  }, [hashState, idToken, roleMappings])

  // Handle SSO errors
  useEffect(() => {
    if (errorFromHash) {
      setSsoError({
        error: errorFromHash,
        errorDescription: errorDescriptionFromHash,
      })
      // Clear the hash state after processing
      navigate(location.pathname + location.search, { replace: true })

      setTimeout(() => {
        window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
      }, 100) // wait for navigate() to complete
    }
  }, [errorFromHash, errorDescriptionFromHash])

  const initiateTestRoleMappings = ({
    roleMappingNameToRemove,
  }: {
    roleMappingNameToRemove: string
  }) => {
    testRoleMappings(
      {
        organizationId,
        idToken: idToken ?? '',
        mappings: roleMappings.filter((rm) => rm.name !== roleMappingNameToRemove),
      },
      {
        onSuccess: (data) => {
          if (
            isOrgOwnerRoleAssignments({
              roleAssignments: data.role_assignments,
              organizationId,
            })
          ) {
            deleteRoleMapping(roleMappingNameToRemove)
          }
        },
        onError: () => {
          addToast({
            family: 'organization.security.role-mappings-table.remove',
            color: 'danger',
            iconType: 'alert',
            title: (
              <FormattedMessage
                {...messages.toastErrorTestRoleMapping}
                values={{ roleMappingName: roleMappingNameToRemove }}
              />
            ),
          })
        },
      },
    )
  }

  const hasToRunValidation = React.useMemo(
    () =>
      checkHasToRunValidation({
        roleMappings,
        selectedRoleMapping,
        organizationId,
      }),
    [organizationId, roleMappings, selectedRoleMapping],
  )

  // Handle test result scrolling
  useEffect(() => {
    if (testResult) {
      window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
    }
  }, [testResult])

  const onClickRemoveRoleMapping = (roleMapping: RoleMapping) => {
    setSelectedRoleMapping(roleMapping)
    setIsConfirmationModalOpen(true)
  }

  const onInitiateSso = () => {
    if (idpInfo?.login_identifier) {
      initiateOrganizationSsoRedirect({
        redirectUri: `${window.location.protocol}//${window.location.host}/account/idp`,
        loginIdentifier: idpInfo.login_identifier,
        state: JSON.stringify({
          roleMappingNameToRemove: selectedRoleMapping?.name,
        }),
      })
    }
  }

  const deleteRoleMapping = (roleMappingName: string) => {
    deleteRoleMappingMutate(
      { organizationId, roleNames: roleMappingName },
      {
        ...afterRemovingRoleMapping({
          removedMappingName: roleMappingName,
          setError,
          onSettled: () => {
            setIsConfirmationModalOpen(false)
          },
        }),
      },
    )
  }

  const onRemoveRoleMapping = () => {
    setError(null)
    resetTestResult()

    if (!organizationId || !selectedRoleMapping) {
      return
    }

    if (hasToRunValidation) {
      onInitiateSso()
    } else {
      deleteRoleMapping(selectedRoleMapping.name)
    }
  }

  const onClickEditRoleMapping = (roleMapping: RoleMapping) => {
    navigate(`${organizationRoleMappingUrl()}/${roleMapping.name}`)
  }

  const columns: Array<EuiBasicTableColumn<RoleMapping>> = [
    {
      field: 'name',
      sortable: true,
      name: formatMessage(messages.nameColumn),
      render: (name: string) =>
        hasUpdateRoleMappingPermission && !readonly ? (
          <EuiButtonEmpty
            flush='left'
            onClick={() => navigate(`${organizationRoleMappingUrl()}/${name}`)}
          >
            <EuiText size='s'>{name}</EuiText>
          </EuiButtonEmpty>
        ) : (
          <EuiText size='s'>{name}</EuiText>
        ),
    },
    {
      field: 'enabled',
      name: formatMessage(messages.statusColumn),
      render: (enabled: boolean) => (
        <Fragment>
          <EuiHealth color={enabled ? 'success' : 'subdued'} />
          <EuiText size='s'>
            {enabled
              ? formatMessage(messages.enabledStatus)
              : formatMessage(messages.disabledStatus)}
          </EuiText>
        </Fragment>
      ),
    },
    {
      field: 'role_assignments',
      name: formatMessage(messages.rolesColumn),
      render: (role_assignments: RoleAssignments) => (
        <RoleBadges roleAssignments={role_assignments} organizationId={organizationId} />
      ),
    },
  ]

  if (!readonly) {
    columns.push({
      name: formatMessage(messages.actionsColumn),
      align: 'right',
      actions: [
        {
          name: formatMessage(messages.editRoleMapping),
          description: formatMessage(messages.editRoleMapping),
          icon: 'pencil',
          color: 'primary',
          type: 'icon',
          isPrimary: true,
          onClick: onClickEditRoleMapping,
          enabled: () => hasUpdateRoleMappingPermission,
        },
        {
          name: formatMessage(messages.removeRoleMapping),
          description: formatMessage(messages.removeRoleMapping),
          icon: 'trash',
          color: 'danger',
          type: 'icon',
          onClick: onClickRemoveRoleMapping,
          enabled: () => hasDeleteRoleMappingPermission && hasUpdateRoleMappingPermission,
        },
      ],
    })
  }

  return (
    <Fragment>
      {error && (
        <div>
          <EuiCallOut color='danger' iconType='alert' size='s' heading='h6' title={error} />
          <EuiSpacer size='l' />
        </div>
      )}
      {ssoError && (
        <div>
          <SsoError
            error={ssoError.error}
            errorDescription={ssoError.errorDescription ?? undefined}
            fromLogin={false}
          />
          <EuiSpacer size='l' />
        </div>
      )}
      {testResult &&
        !isOrgOwnerRoleAssignments({
          roleAssignments: testResult.role_assignments,
          organizationId,
        }) && (
          <VerificationResult
            testResult={testResult}
            organizationId={organizationId}
            type='Delete'
            roleMappingName={selectedRoleMapping?.name ?? ''}
          />
        )}
      <EuiInMemoryTable
        loading={isLoading || isLoadingIdp}
        noItemsMessage={
          (isLoading && formatMessage(messages.loadingRoleMappings)) ||
          (roleMappings.length === 0 && formatMessage(messages.noRoleMappings))
        }
        columns={columns}
        items={roleMappings}
        sorting={sorting}
      />
      {isConfirmationModalOpen && (
        <ConfirmRemovingRoleMappingModal
          roleMappingName={selectedRoleMapping?.name ?? ''}
          isLoading={isDeletingRoleMapping}
          onRemoveRoleMapping={onRemoveRoleMapping}
          onClose={() => setIsConfirmationModalOpen(false)}
          hasToRunValidation={hasToRunValidation}
          samlSsoEnabled={!!idpInfo?.configuration.enabled}
        />
      )}
    </Fragment>
  )
}

export default RoleMappingTable
